import { compact, forEach, isEmpty, map, merge, reduce } from 'lodash';
import { epoch, formatEpochAsDate, utc } from '../../util/datetime';
import { sanitizeHtml, toUUID } from '../../util/transform';
import ChecklistItem from '../checklist/ChecklistItem';
import Document from '../document/Document';
import Field from '../field/Field';
import Asset from '../finance/Asset';
import AssetFinanceAssetCommitmentSchedule from '../finance/AssetFinanceAssetCommitmentSchedule';
import Expense from '../finance/Expense';
import Household from '../finance/Household';
import Income from '../finance/Income';
import Insurance from '../finance/Insurance';
import Liability from '../finance/Liability';
import Objective from '../finance/Objective';
import OtherAdviser from '../finance/OtherAdviser';
import { ColorType } from '../global/Color.enum';
import { DocType } from '../global/Doc.enum';
import OpportunityContextual from '../opportunity/OpportunityContextual';
import Root from '../Root';
import type { ITicket } from './Ticket.interface';
import TicketAssetFinance from './TicketAssetFinance';
import TicketCommercialLoan from './TicketCommercialLoan';
import TicketContactType from './TicketContactType';
import TicketConveyancingLoan from './TicketConveyancingLoan';
import TicketHomeLoan from './TicketHomeLoan';
import TicketInsurance from './TicketInsurance';
import TicketIntegrationHistory from './TicketIntegrationHistory';
import TicketOpenBanking from './TicketOpenBanking';
import TicketPersonalLoan from './TicketPersonalLoan';
import TicketProjectHistory from './TicketProjectHistory';
import TicketRealEstate from './TicketRealEstate';
import TicketSharedWith from './TicketSharedWith';
import TicketStageHistory from './TicketStageHistory';
import TicketStatusHistory from './TicketStatusHistory';
import TicketValues from './TicketValues';

export default class Ticket extends Root implements ITicket {
  ref: string;
  uuid: string;
  idWorkflow: string;
  idStage: string;
  idTeamMembers: Array<string>;
  idClients: Array<string>;
  ticketClientTypes: Array<TicketContactType>;
  idContacts: Array<string>;
  ticketContactTypes: Array<TicketContactType>;
  idPartners: Array<string>;
  idAccountsAsPartners: Array<string>;
  partnersReferralFee: any;
  idAssetFinance: string;
  idLoan: string;
  idRiskInsurance: string;
  name: string;
  description: string;
  idLabels: Array<string>;
  tileColor: ColorType;
  values: TicketValues;
  customFields: Array<Field>;
  assetFinance: TicketAssetFinance;
  insurance: TicketInsurance;
  loan: TicketHomeLoan;
  commercialLoan: TicketCommercialLoan;
  personalLoan: TicketPersonalLoan;
  conveyancing: TicketConveyancingLoan;
  realEstate: TicketRealEstate;
  income: Array<Income>;
  expenses: Expense;
  assets: Array<Asset>;
  liabilities: Array<Liability>;
  objectives: Objective;
  insurances: Array<Insurance>;
  integrations: {
    flexId: string,
    pepperResolve: {
      internalId: string,
      statusProcessing: string,
      description: string
    },
    basePlate: {
      accountRef: string,
      loans: Array<any>
    },
    movingHub: {
      customerId: string,
      referenceCode: string
    },
    mercuryId: string,
    myConnectId: string,
    aliId: string,
    driveOnline: {
      applicationId: string,
      applicationName: string
    }
  };

  assetCommitmentSchedules: Array<AssetFinanceAssetCommitmentSchedule>;
  otherAdvisers: Array<OtherAdviser>;
  checklist: Array<ChecklistItem>;
  attachments: Array<Document>;
  projectHistory: Array<TicketProjectHistory>;
  stageHistory: Array<TicketStageHistory>;
  integrationHistory: Array<TicketIntegrationHistory>;
  statusHistory: Array<TicketStatusHistory>;
  sharedWith: TicketSharedWith;
  diversificationOpportunities: Array<OpportunityContextual>;
  diversificationOpportunitiesComments: string;
  dropboxSyncs: Array<any>;
  googleDriveSyncs: Array<any>;
  oneDriveSyncs: Array<any>;
  openBanking: Array<TicketOpenBanking>;
  isDueAhead: boolean;
  isDue: boolean;
  isOverdue: boolean;
  isFinanceDueDateAhead: boolean;
  isFinanceDueDateReached: boolean;
  isFinanceDueDateOverdue: boolean;
  isInProgress: boolean;
  isReadyForVerification: boolean;
  isVerified: boolean;
  isFrozen: boolean;
  frozenTimeStamp: number;
  isRotten: boolean;
  rottenTimeStamp: number;
  isSubmitted: boolean;
  submittedTimeStamp: number;
  isLodged: boolean;
  lodgedTimeStamp: number;
  isConditionallyApproved: boolean;
  conditionallyApprovedTimeStamp: number;
  isApproved: boolean;
  approvedTimeStamp: number;
  isSettled: boolean;
  settledTimeStamp: number;
  isWon: boolean;
  wonTimeStamp: number;
  isWithDrawn: boolean;
  withDrawnTimeStamp: number;
  isDeclined: boolean;
  declinedTimeStamp: number;
  isLost: boolean;
  lostTimeStamp: number;
  isImported: boolean;
  importedTimeStamp: number;
  deletedTimeStamp: number;

  getName() {
    return this.name;
  }

  getDescription() {
    return this.description;
  }

  getValue(probability) {
    const val = this?.values?.onceOff;
    const prob = parseInt(probability || 0, 10);
    if (prob < 100) {
      return (val * prob) / 100;
    }
    return val;
  }

  getCreatedDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.timeStamp, timeZone, locale, localeFormat);
  }

  getDueDate(timeZone, locale, localeFormat) {
    return this.values?.onceOffDueDate
      ? formatEpochAsDate(this.values?.onceOffDueDate, timeZone, locale, localeFormat)
      : '';
  }

  getFinanceDueDate(timeZone, locale, localeFormat) {
    return this.values?.financeDueDate
      ? formatEpochAsDate(this.values?.financeDueDate, timeZone, locale, localeFormat)
      : '';
  }

  getStatus() {
    let status = '';
    if (
      this.isInProgress &&
      !this.isSettled &&
      !this.isLodged &&
      !this.isWon &&
      !this.isApproved &&
      !this.isConditionallyApproved &&
      !this.isSubmitted
    ) {
      status = 'Open';
    } else if (this.isWon) {
      status = 'Won';
    } else if (this.isInProgress && this.isSettled) {
      status = 'Settled';
    } else if (this.isLost) {
      status = 'Lost';
    } else if (this.isFrozen) {
      status = 'Paused';
    } else if (this.isApproved) {
      status = 'Approved';
    } else if (this.isConditionallyApproved) {
      status = 'Conditionally Approved';
    } else if (this.isWithDrawn) {
      status = 'Withdrawn';
    } else if (this.isDeclined) {
      status = 'Declined';
    } else if (this.isSubmitted) {
      status = 'Submitted';
    } else if (this.isLodged) {
      status = 'Lodged';
    } else if (!this.isActive) {
      status = 'Deleted';
    }
    return status;
  }

  totalLoanAmount() {
    return reduce(
      this?.loan?.details?.dealLoanDetailsSplit,
      (memo, num) =>
        memo + (!Number.isNaN(parseFloat(num.amount)) ? parseFloat(num.amount) : 0),
      0
    );
  }

  totalFees() {
    let total = 0;
    forEach(this?.loan?.fees, (value) => {
      total += !Number.isNaN(parseFloat(value)) ? parseFloat(value) : 0;
    });
    return total;
  }

  totalCommercialLoanAmount() {
    return reduce(
      this?.commercialLoan?.details?.dealCommercialLoanDetailsSplit,
      (memo, num) =>
        memo + (!Number.isNaN(parseFloat(num.amount)) ? parseFloat(num.amount) : 0),
      0
    );
  }

  totalCommercialFees() {
    let total = 0;
    forEach(this?.commercialLoan?.fees, (value) => {
      total += !Number.isNaN(parseFloat(value)) ? parseFloat(value) : 0;
    });
    return total;
  }

  totalConveyancingFeesAmount() {
    return reduce(
      this?.conveyancing?.fees,
      (memo, num) =>
        memo + (!Number.isNaN(parseFloat(num.amount)) ? parseFloat(num.amount) : 0),
      0
    );
  }

  totalRealEstateFeesAmount() {
    return reduce(
      this?.realEstate?.fees,
      (memo, num) =>
        memo + (!Number.isNaN(parseFloat(num.amount)) ? parseFloat(num.amount) : 0),
      0
    );
  }

  static build = (data: ITicket) => new Ticket(data);
  static blank = () => new Ticket({ isInProgress: true, isActive: true });
  static blankStatus = () => ({
    isDueAhead: false,
    isDue: false,
    isOverdue: false,
    isFinanceDueDateAhead: false,
    isFinanceDueDateReached: false,
    isFinanceDueDateOverdue: false,
    isInProgress: false,
    isReadyForVerification: false,
    isVerified: false,
    isFrozen: false,
    frozenTimeStamp: 0,
    isRotten: false,
    rottenTimeStamp: 0,
    isSubmitted: false,
    submittedTimeStamp: 0,
    isLodged: false,
    lodgedTimeStamp: 0,
    isConditionallyApproved: false,
    conditionallyApprovedTimeStamp: 0,
    isApproved: false,
    approvedTimeStamp: 0,
    isSettled: false,
    settledTimeStamp: 0,
    isWon: false,
    wonTimeStamp: 0,
    isWithDrawn: false,
    withDrawnTimeStamp: 0,
    isDeclined: false,
    declinedTimeStamp: 0,
    isLost: false,
    lostTimeStamp: 0,
    // isImported: false,
    // importedTimeStamp: 0,
    deletedTimeStamp: 0
  });

  static setStatus = (status: string, data: ITicket, props: any) => {
    let statusProps = {};
    switch (status) {
      case 'lost':
        statusProps = {
          isLost: true,
          isActive: true
        };
        break;
      case 'paused':
        statusProps = {
          isFrozen: true,
          isActive: true
        };
        break;
      case 'won':
        statusProps = {
          isWon: true,
          isSettled: true,
          isActive: true
        };
        break;
      case 'settled':
        statusProps = {
          isSettled: true,
          isInProgress: true,
          isActive: true
        };
        break;
      case 'deleted':
        statusProps = {
          isActive: false
        };
        break;
      case 'inProgress':
        statusProps = {
          isInProgress: true,
          isActive: true
        };
        break;
      default:
        statusProps = {};
    }
    return merge(data, this.blankStatus(), statusProps, props);
  };

  /**
   * Constructor
   * @param {ITicket} data
   */
  constructor(data?: ITicket) {
    super(DocType.of('__datum.ticket'), data);

    this.ref = data?.ref || '';
    this.uuid = data?.uuid || '';
    this.idWorkflow = toUUID(data?.idWorkflow);
    this.idStage = toUUID(data?.idStage);
    this.idTeamMembers = compact(map(data?.idTeamMembers, (obj) => toUUID(obj)));
    this.idClients = compact(map(data?.idClients, (obj) => toUUID(obj)));
    this.ticketClientTypes = compact(
      map(data?.ticketClientTypes, (obj) => new TicketContactType(obj))
    );
    this.idContacts = compact(map(data?.idContacts, (obj) => toUUID(obj)));
    this.ticketContactTypes = compact(
      map(data?.ticketContactTypes, (obj) => new TicketContactType(obj))
    );
    this.idPartners = compact(map(data?.idPartners, (obj) => toUUID(obj)));
    this.idAccountsAsPartners = compact(map(data?.idAccountsAsPartners, (obj) => toUUID(obj)));
    this.partnersReferralFee = data?.partnersReferralFee || undefined;
    this.idAssetFinance = toUUID(data?.idAssetFinance);
    this.idLoan = toUUID(data?.idLoan);
    this.idRiskInsurance = toUUID(data?.idRiskInsurance);
    this.name = data?.name || '';
    this.description = sanitizeHtml(data?.description || '');
    this.idLabels = compact(map(data?.idLabels, (obj) => toUUID(obj)));
    // TODO: data?.color?.id is legacy data structure
    this.tileColor = ColorType.of(
      data?.tileColor?.name
        ? data?.tileColor?.name
        : `${data?.tileColor?.id || ''}_${data?.tileColor?.variation || '500'}`
    );
    // TODO: data?.color?.id is legacy data structure
    this.values = new TicketValues(data?.values);
    this.customFields = map(data?.customFields, (obj) => new Field(obj));
    this.assetFinance = !isEmpty(data?.assetFinance)
      ? new TicketAssetFinance(data?.assetFinance)
      : {};
    this.insurance = !isEmpty(data?.insurance) ? new TicketInsurance(data?.insurance) : {};
    this.loan = !isEmpty(data?.loan) ? new TicketHomeLoan(data?.loan) : {};
    this.commercialLoan = !isEmpty(data?.commercialLoan)
      ? new TicketCommercialLoan(data?.commercialLoan)
      : {};
    this.personalLoan = !isEmpty(data?.personalLoan)
      ? new TicketPersonalLoan(data?.personalLoan)
      : {};
    this.conveyancing = !isEmpty(data?.conveyancing)
      ? new TicketConveyancingLoan(data?.conveyancing)
      : {};
    this.realEstate = !isEmpty(data?.realEstate) ? new TicketRealEstate(data?.realEstate) : {};
    this.income = map(data?.income, (obj) => new Income(obj));
    this.expenses = map(compact(data?.expenses), (obj) =>
      !obj?.id ? new Expense(obj) : new Household(obj)
    );
    this.assets = map(data?.assets, (obj) => new Asset(obj));
    this.liabilities = map(data?.liabilities, (obj) => new Liability(obj));
    this.objectives = new Objective(data?.objectives);
    this.insurances = map(data?.insurances, (obj) => new Insurance(obj));
    this.integrations = {
      flexId: data?.integrations?.flexId || undefined,
      pepperResolve: {
        internalId: data?.integrations?.pepperResolve?.internalId || undefined,
        statusProcessing: data?.integrations?.pepperResolve?.statusProcessing || undefined,
        description: data?.integrations?.pepperResolve?.description || undefined
      },
      basePlate: {
        accountRef: data?.integrations?.basePlate?.accountRef || '',
        loans: data?.integrations?.basePlate?.loans || []
      },
      movingHub: {
        customerId: data?.integrations?.movingHub?.customerId,
        referenceCode: data?.integrations?.movingHub?.referenceCode
      },
      // TODO: data?.mercuryId is legacy data structure
      mercuryId: data?.integrations?.mercuryId || data?.mercuryId,
      // TODO: data?.myConnectId is legacy data structure
      myConnectId: data?.integrations?.myConnectId || data?.myConnectId,
      // TODO: data?.aliId is legacy data structure
      aliId: data?.integrations?.aliId || data?.aliId,
      driveOnline: {
        applicationId: data?.integrations?.driveOnline?.applicationId || undefined,
        applicationName: data?.integrations?.driveOnline?.applicationName || undefined
      }
    };

    this.otherAdvisers = map(data?.otherAdvisers, (obj) => new OtherAdviser(obj));
    this.checklist = map(data?.checklist, (obj) => new ChecklistItem(obj));
    this.attachments = map(data?.attachments, (obj) => new Document(obj));
    this.projectHistory = map(data?.projectHistory, (obj) => new TicketProjectHistory(obj));
    this.stageHistory = map(data?.stageHistory, (obj) => new TicketStageHistory(obj));
    this.integrationHistory = map(
      data?.integrationHistory,
      (obj) => new TicketIntegrationHistory(obj)
    );
    this.statusHistory = map(data?.statusHistory, (obj) => new TicketStatusHistory(obj));
    this.sharedWith = new TicketSharedWith(data?.sharedWith);
    this.diversificationOpportunities = compact(
      map(data?.diversificationOpportunities, (obj) => new OpportunityContextual(obj))
    );
    this.diversificationOpportunitiesComments =
      data?.diversificationOpportunitiesComments || '';
    this.dropboxSyncs = data?.dropboxSyncs || [];
    this.googleDriveSyncs = data?.googleDriveSyncs || [];
    this.oneDriveSyncs = data?.oneDriveSyncs || [];
    this.assetCommitmentSchedules = compact(
      map(data?.assetCommitmentSchedules, (obj) => new AssetFinanceAssetCommitmentSchedule(obj))
    );
    this.openBanking = map(compact(data?.openBanking), (obj) => new TicketOpenBanking(obj));
    this.isDueAhead = data?.isDueAhead === true;
    this.isDue = data?.isDue === true;
    this.isOverdue = data?.isOverdue === true;
    this.isFinanceDueDateAhead = data?.isFinanceDueDateAhead === true;
    this.isFinanceDueDateReached = data?.isFinanceDueDateReached === true;
    this.isFinanceDueDateOverdue = data?.isFinanceDueDateOverdue === true;
    this.isInProgress = data?.isInProgress === true;
    this.isReadyForVerification = data?.isReadyForVerification === true;
    this.isVerified = data?.isVerified === true;
    this.isFrozen = data?.isFrozen === true;
    this.frozenTimeStamp = data?.frozenTimeStamp ? epoch(utc(data?.frozenTimeStamp)) : 0;
    this.isRotten = data?.isRotten === true;
    this.rottenTimeStamp = data?.rottenTimeStamp ? epoch(utc(data?.rottenTimeStamp)) : 0;
    this.isSubmitted = data?.isSubmitted === true;
    this.submittedTimeStamp = data?.submittedTimeStamp
      ? epoch(utc(data?.submittedTimeStamp))
      : 0;
    this.isLodged = data?.isLodged === true;
    this.lodgedTimeStamp = data?.lodgedTimeStamp ? epoch(utc(data?.lodgedTimeStamp)) : 0;
    this.isConditionallyApproved = data?.isConditionallyApproved === true;
    this.conditionallyApprovedTimeStamp = data?.conditionallyApprovedTimeStamp
      ? epoch(utc(data?.conditionallyApprovedTimeStamp))
      : 0;
    this.isApproved = data?.isApproved === true;
    this.approvedTimeStamp = data?.approvedTimeStamp ? epoch(utc(data?.approvedTimeStamp)) : 0;
    this.isSettled = data?.isSettled === true;
    this.settledTimeStamp = data?.settledTimeStamp ? epoch(utc(data?.settledTimeStamp)) : 0;
    this.isWon = data?.isWon === true;
    this.wonTimeStamp = data?.wonTimeStamp ? epoch(utc(data?.wonTimeStamp)) : 0;
    this.isWithDrawn = data?.isWithDrawn === true;
    this.withDrawnTimeStamp = data?.withDrawnTimeStamp
      ? epoch(utc(data?.withDrawnTimeStamp))
      : 0;
    this.isDeclined = data?.isDeclined === true;
    this.declinedTimeStamp = data?.declinedTimeStamp ? epoch(utc(data?.declinedTimeStamp)) : 0;
    this.isLost = data?.isLost === true;
    this.lostTimeStamp = data?.lostTimeStamp ? epoch(utc(data?.lostTimeStamp)) : 0;
    this.isImported = data?.isImported === true;
    this.importedTimeStamp = data?.importedTimeStamp ? epoch(utc(data?.importedTimeStamp)) : 0;
    this.deletedTimeStamp = data?.deletedTimeStamp ? epoch(utc(data?.deletedTimeStamp)) : 0;
  }
}
